import React, { createContext, useContext, useState } from "react";

import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/system";
import {
  coreDarkTheme,
  coreLightTheme,
  gravityDarkTheme,
  gravityLightTheme,
  miromaDarkTheme,
  miromaLightTheme,
} from "./clients";

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const Theme = createContext<ThemeContextType | undefined>(undefined);

declare module "@mui/material/styles" {
  interface Palette {
    score: Palette["primary"];
    gradient: string[];
    segments: string[];
    segment1: Palette["primary"];
    segment2: Palette["primary"];
    segment3: Palette["primary"];
    segment4: Palette["primary"];
    segment5: Palette["primary"];
    segment6: Palette["primary"];
  }

  interface PaletteOptions {
    score?: PaletteOptions["primary"];
    gradient?: string[];
    segments?: string[];
    segment1?: PaletteOptions["primary"];
    segment2?: PaletteOptions["primary"];
    segment3?: PaletteOptions["primary"];
    segment4?: PaletteOptions["primary"];
    segment5?: PaletteOptions["primary"];
    segment6?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsColorOverrides {
    [key: `segment${number}`]: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    [key: `segment${number}`]: true;
  }
}


export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [darkMode, setDarkMode] = useState(true);

  const client = process.env.REACT_APP_CLIENT || 'core';
  let darkTheme = undefined;
  let lightTheme = undefined;

  if (client === 'gravity') {
    darkTheme = createTheme(gravityDarkTheme);
    lightTheme = createTheme(gravityLightTheme);
  } else if (client === 'miroma') {
    darkTheme = createTheme(miromaDarkTheme);
    lightTheme = createTheme(miromaLightTheme);
  } else {
    darkTheme = createTheme(coreDarkTheme);
    lightTheme = createTheme(coreLightTheme);
  }

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <Theme.Provider value={{ darkMode, toggleDarkMode }}>
        {children}
      </Theme.Provider>
    </ThemeProvider>
  );
};

export const useThemeMode = () => {
  const context = useContext(Theme);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
